import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import { companyInfo, trustAndSafetySections } from './trustAndSafetyContent';

import css from './TrustAndSafetyPage.module.css';

import { ReactComponent as DownloadIcon } from '../../assets/svg/downloadIcon.svg';

const Paragraph = ({ id }) => (
  <p className={css.paragraph}>
    <FormattedMessage id={id} />
  </p>
);

const BulletList = ({ items }) => (
  <ul className={css.bulletList}>
    {items.map((item, index) => (
      <li key={index}>
        <span className={css.bulletLead}>
          <FormattedMessage id={item.boldId} />
        </span>{' '}
        — <FormattedMessage id={item.restId} />
      </li>
    ))}
  </ul>
);

const Callout = ({ id, email }) => (
  <div className={css.callout}>
    <p className={css.calloutText}>
      {id ? <FormattedMessage id={id} /> : null}
      {id && email ? ' ' : null}
      {email ? (
        <a className={css.emailLink} href={`mailto:${email}`}>
          {email}
        </a>
      ) : null}
    </p>
  </div>
);

const Block = ({ block }) => {
  switch (block.type) {
    case 'p':
      return <Paragraph id={block.id} />;
    case 'ul':
      return <BulletList items={block.items} />;
    case 'callout':
      return <Callout id={block.id} email={block.email} />;
    default:
      return null;
  }
};

const Section = ({ section }) => {
  const content = (
    <>
      <h2 className={css.sectionHeading}>
        <FormattedMessage id={section.titleId} />
      </h2>
      {section.blocks.map((block, index) => (
        <Block block={block} key={index} />
      ))}
    </>
  );

  return (
    <div className={css.section}>
      {section.gradient ? (
        <div className={css.gradientBlock}>{content}</div>
      ) : section.accentBar ? (
        <div className={css.accentBarBlock}>{content}</div>
      ) : (
        content
      )}
    </div>
  );
};

/**
 * Content-only rendering of the Trust & Safety page, meant for embedding
 * elsewhere on the site.
 */
export const TrustAndSafetyContent = () => (
  <div className={css.card}>
    {trustAndSafetySections.map((section, index) => (
      <Section section={section} key={index} />
    ))}
  </div>
);

export const TrustAndSafetyPageComponent = props => {
  const { scrollingDisabled } = props;
  const history = useHistory();
  const intl = useIntl();

  const handleDownloadPdf = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  const pageTitle = intl.formatMessage({ id: 'TrustAndSafetyPage.pageTitle' });

  return (
    <Page title={pageTitle} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.heroSection}>
            <div className={css.topRow}>
              <button className={css.backLink} onClick={() => history.goBack()} type="button">
                <FormattedMessage id="TrustAndSafetyPage.backBtn" />
              </button>
              <button className={css.downloadButton} onClick={handleDownloadPdf} type="button">
                <DownloadIcon className={css.icon} />
                <FormattedMessage id="TrustAndSafetyPage.downloadBtn" />
              </button>
            </div>

            <h1 className={css.title}>{pageTitle}</h1>
            <div className={css.subtitleRow}>
              <span>{companyInfo.name}</span>
              <span className={css.subtitleDivider}>|</span>
              <span>
                <FormattedMessage id="TrustAndSafetyPage.effectiveDate" />: {companyInfo.effectiveDate}
              </span>
            </div>
            <p className={css.intro}>
              <FormattedMessage id="TrustAndSafetyPage.intro" />
            </p>
          </div>

          <div className={css.content}>
            <TrustAndSafetyContent />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const TrustAndSafetyPage = connect(mapStateToProps)(TrustAndSafetyPageComponent);

export default TrustAndSafetyPage;
